import type { Antworten } from "@fbs/funnel-schema";

/**
 * Zwischenspeicher im Browser.
 *
 * Wer auf dem Handy mitten im Funnel einen Anruf bekommt, kommt oft auf eine
 * neu geladene Seite zurück. Ohne Zwischenspeicher fängt er von vorn an, und
 * das tut fast niemand. Gespeichert wird nur lokal und nur so lange, wie der
 * Funnel es vorgibt.
 *
 * Achtung: Hier stehen Kontaktdaten drin, die der Nutzer eingetippt, aber noch
 * nicht abgeschickt hat. Deshalb wird der Eintrag beim Absenden gelöscht und
 * läuft ohnehin von selbst ab.
 */

export interface Sitzungsstand {
  aktuell: string;
  antworten: Antworten;
  besucht: string[];
  begonnenAm: number;
  gespeichertAm: number;
}

function schluessel(funnelId: string): string {
  return `fbs.funnel.${funnelId}`;
}

function verfuegbar(): boolean {
  try {
    // Im privaten Modus mancher Browser wirft schon der Zugriff.
    const probe = "__fbs__";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

export function standSpeichern(funnelId: string, stand: Sitzungsstand): void {
  if (!verfuegbar()) return;
  try {
    window.localStorage.setItem(schluessel(funnelId), JSON.stringify(stand));
  } catch {
    // Voller Speicher darf den Funnel nicht anhalten.
  }
}

export function standLaden(
  funnelId: string,
  gueltigStunden: number,
): Sitzungsstand | null {
  if (!verfuegbar()) return null;

  try {
    const roh = window.localStorage.getItem(schluessel(funnelId));
    if (!roh) return null;

    const stand = JSON.parse(roh) as Partial<Sitzungsstand>;
    if (
      typeof stand.aktuell !== "string" ||
      typeof stand.gespeichertAm !== "number" ||
      typeof stand.antworten !== "object" ||
      stand.antworten === null
    ) {
      return null;
    }

    const alter = Date.now() - stand.gespeichertAm;
    if (alter > gueltigStunden * 60 * 60 * 1000) {
      standLoeschen(funnelId);
      return null;
    }

    return {
      aktuell: stand.aktuell,
      antworten: stand.antworten,
      besucht: Array.isArray(stand.besucht) ? stand.besucht : [stand.aktuell],
      begonnenAm: typeof stand.begonnenAm === "number" ? stand.begonnenAm : Date.now(),
      gespeichertAm: stand.gespeichertAm,
    };
  } catch {
    return null;
  }
}

export function standLoeschen(funnelId: string): void {
  if (!verfuegbar()) return;
  try {
    window.localStorage.removeItem(schluessel(funnelId));
  } catch {
    // Nicht schlimm: Der Eintrag läuft ohnehin ab.
  }
}
